import { useLocation, useParams } from '@solidjs/router';
import { createEffect, createRenderEffect, type JSX, onCleanup, Show } from 'solid-js';
import { setActiveAccountId } from '../lib/active-account';
import { isForbidden } from '../lib/api';
import { writeLastOrg } from '../lib/last-org';
import {
  findMembership,
  isReachable,
  membershipSegment,
  type UncoveredOrg,
  uncoveredOrg,
} from '../lib/memberships';
import { decodeOrgSegment, setUrlOrg, urlOrg } from '../lib/org';
import { SESSION_TOKEN, useMe, useSessionMe } from '../lib/session';
import { OrgDenied } from './OrgDenied';

/**
 * Route wrapper for `/org/:org` (#460). Mirrors the URL org segment into the
 * two module signals the rest of the app already hangs off:
 *
 *   - `urlOrg` — drives `orgHref` links and the `X-Dz-Account` request header.
 *   - `activeAccountId` — the signal EVERY account-scoped query keys on, so the
 *     URL org feeds the exact re-keying mechanism the session switcher used:
 *     `/org/a` and `/org/b` produce distinct query keys and never share cache.
 *
 * A render effect (not `createEffect`) so the signals are set synchronously
 * before any child route renders and fires its first query.
 *
 * It also owns the org's REFUSAL: when the org-scoped `/v1/me` answers 403 (the
 * API refused the `X-Dz-Account` claim), the page is `<OrgDenied/>` rather than
 * a screenful of panels each denied on its own. The app gate never sees this
 * read (it probes `useSessionMe`), so the shell stays mounted. `token` is the
 * credential `<App/>` resolved; the same `useMe` key every org screen joins.
 * A refused org the SESSION's memberships hold as unreachable (an SSO-pinned
 * sign-in) is not "not a member": `<OrgDenied/>` then names what this sign-in
 * covers and the org's own sign-in (`uncoveredOrg`, `lib/memberships.ts`).
 */
export function OrgScope(props: {
  readonly token?: string | null;
  readonly children?: JSX.Element;
}): JSX.Element {
  const params = useParams();
  const location = useLocation();
  createRenderEffect(() => {
    const raw = params['org'];
    const org = raw ? decodeOrgSegment(raw) : null;
    setUrlOrg(org);
    setActiveAccountId(org);
  });
  // Leaving the org scope (e.g. onto /admin) clears both — links fall back to
  // bare paths and queries to the "no named org" key.
  onCleanup(() => {
    setUrlOrg(null);
    setActiveAccountId(null);
  });
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const me = useMe(token);
  const sessionMe = useSessionMe(token);
  // #466 § 6: an org page that OPENED is the last org chosen — an explicit
  // `/org/<login>` link wins over the remembered one and refreshes it. Only once
  // the server accepted the claim (an org-scoped `me` for this URL, no 403), and
  // stored as the membership's own login, never the URL's casing or a typo.
  createEffect(() => {
    const org = urlOrg();
    if (org === null || me.isError || me.isPlaceholderData || me.data === undefined) return;
    const m = findMembership(me.data.memberships ?? [], org);
    if (m !== undefined && isReachable(m)) writeLastOrg(membershipSegment(m));
  });
  const denied = (): string | null => {
    const org = urlOrg();
    return org !== null && isForbidden(me.error) ? org : null;
  };
  const uncovered = (org: string): UncoveredOrg | null =>
    uncoveredOrg(
      sessionMe.data?.memberships ?? [],
      org,
      `${location.pathname}${location.search}`,
      window.location.origin,
    );
  return (
    <Show when={denied()} fallback={props.children}>
      {(org) => <OrgDenied org={org()} error={me.error} uncovered={uncovered(org())} />}
    </Show>
  );
}
